import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const BulkEmailSection = () => {
  const [activeTab, setActiveTab] = useState('compose');
  const [subscribers, setSubscribers] = useState([]);
  const [selectedSubscribers, setSelectedSubscribers] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sentEmails, setSentEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showEmailDetails, setShowEmailDetails] = useState(false);

  // Fetch subscribers on component mount
  useEffect(() => {
    fetchSubscribers();
    fetchSentEmails();
  }, []);

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/subscriptions/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("access_token") || ""}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSubscribers(data);
        // Select all subscribers by default
        setSelectedSubscribers(data.map(sub => sub.id));
      } else {
        setError('Failed to fetch subscribers');
      }
    } catch (err) {
      setError('Network error while fetching subscribers');
    } finally {
      setLoading(false);
    }
  };

  const fetchSentEmails = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sent-emails/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("access_token") || ""}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSentEmails(data);
      } else {
        console.error('Failed to fetch sent emails');
      }
    } catch (err) {
      console.error('Network error while fetching sent emails:', err);
    }
  };

  const toggleSubscriber = (subscriberId) => {
    setSelectedSubscribers(prev => 
      prev.includes(subscriberId)
        ? prev.filter(id => id !== subscriberId)
        : [...prev, subscriberId]
    );
  };

  const selectAll = () => {
    setSelectedSubscribers(subscribers.map(sub => sub.id));
  };

  const deselectAll = () => {
    setSelectedSubscribers([]);
  };

  const handleSendEmail = async () => {
    if (!subject.trim() || !body.trim()) {
      setError('Please fill in both subject and body');
      return;
    }

    if (selectedSubscribers.length === 0) {
      setError('Please select at least one subscriber');
      return;
    }

    setSending(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/api/send-bulk-email/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem("access_token") || ""}`,
        },
        body: JSON.stringify({
          subject,
          body,
          recipient_ids: selectedSubscribers
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSuccess(`Email sent successfully to ${result.sent_count} subscribers!`);
        setSubject('');
        setBody('');
        setSelectedSubscribers([]);
        fetchSentEmails(); // Refresh sent emails list
      } else {
        setError(result.message || 'Failed to send email. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please try again later.');
    } finally {
      setSending(false);
    }
  };

  const viewEmailDetails = async (emailId) => {
    try {
      const response = await fetch(`${API_URL}/api/sent-emails/${emailId}/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("access_token") || ""}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedEmail(data);
        setShowEmailDetails(true);
      } else {
        console.error('Failed to fetch email details');
      }
    } catch (err) {
      console.error('Network error while fetching email details:', err);
    }
  };

  const closeEmailDetails = () => {
    setShowEmailDetails(false);
    setSelectedEmail(null);
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const renderComposeTab = () => (
    <div className="compose-email-section">
      <div className="email-form" style={{ marginBottom: '30px' }}>
        <div className="form-group" style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Subject
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Enter email subject"
            className="input"
            style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #ddd' }}
          />
        </div>

        <div className="form-group" style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Message Body
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Enter your message"
            rows={8}
            style={{ 
              width: '100%', 
              padding: '12px', 
              borderRadius: '6px', 
              border: '1px solid #ddd',
              fontFamily: 'inherit',
              resize: 'vertical'
            }}
          />
        </div>

        {error && (
          <div style={{ color: '#dc3545', marginBottom: '15px', padding: '10px', backgroundColor: '#f8d7da', borderRadius: '6px' }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ color: '#155724', marginBottom: '15px', padding: '10px', backgroundColor: '#d4edda', borderRadius: '6px' }}>
            {success}
          </div>
        )}
      </div>

      <div className="subscribers-section">
        <div className="subscribers-header" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '20px' 
        }}>
          <h3 style={{ margin: 0 }}>
            Recipients ({selectedSubscribers.length} of {subscribers.length} selected)
          </h3>
          <div className="selection-controls" style={{ display: 'flex', gap: '10px' }}>
            <button 
              className="btn-secondary" 
              onClick={selectAll}
              disabled={loading}
              style={{ padding: '6px 12px', fontSize: '14px' }}
            >
              Select All
            </button>
            <button 
              className="btn-secondary" 
              onClick={deselectAll}
              disabled={loading}
              style={{ padding: '6px 12px', fontSize: '14px' }}
            >
              Deselect All
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div>Loading subscribers...</div>
          </div>
        ) : (
          <div className="subscribers-grid" style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', 
            gap: '15px',
            marginBottom: '30px'
          }}>
            {subscribers.map((subscriber) => {
              const isSelected = selectedSubscribers.includes(subscriber.id);
              return (
                <div
                  key={subscriber.id}
                  onClick={() => toggleSubscriber(subscriber.id)}
                  style={{
                    padding: '15px',
                    border: '2px solid',
                    borderColor: isSelected ? '#007bff' : '#e0e0e0',
                    borderRadius: '8px',
                    backgroundColor: isSelected ? '#f0f8ff' : '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: isSelected ? '0 2px 8px rgba(0,123,255,0.2)' : '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.target.style.backgroundColor = '#f8f9fa';
                      e.target.style.borderColor = '#007bff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.target.style.backgroundColor = '#fff';
                      e.target.style.borderColor = '#e0e0e0';
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '3px',
                      border: '2px solid',
                      borderColor: isSelected ? '#007bff' : '#ccc',
                      backgroundColor: isSelected ? '#007bff' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {isSelected && (
                        <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
                      )}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ 
                        fontSize: '14px', 
                        color: '#666',
                        wordBreak: 'break-word'
                      }}>
                        {subscriber.email}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && subscribers.length === 0 && (
          <div style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
            No subscribers found. Add some subscribers first.
          </div>
        )}

        <div className="send-button-container" style={{ textAlign: 'center' }}>
          <button
            className="btn-primary-primary"
            onClick={handleSendEmail}
            disabled={sending || selectedSubscribers.length === 0 || loading}
            style={{
              padding: '12px 30px',
              fontSize: '16px',
              opacity: sending || selectedSubscribers.length === 0 || loading ? 0.6 : 1,
              cursor: sending || selectedSubscribers.length === 0 || loading ? 'not-allowed' : 'pointer'
            }}
          >
            {sending ? 'Sending...' : `Send Email to ${selectedSubscribers.length} Recipients`}
          </button>
        </div>
      </div>
    </div>
  );

  const renderSentEmailsTab = () => (
    <div className="sent-emails-section">
      <h3 style={{ marginBottom: '20px' }}>Sent Emails History</h3>
      
      {sentEmails.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
          No emails sent yet.
        </div>
      ) : (
        <div className="sent-emails-table">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Subject</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Recipients</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Sent Date</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {sentEmails.map((email) => (
                <tr key={email.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '12px' }}>
                    <button
                      onClick={() => viewEmailDetails(email.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#007bff',
                        cursor: 'pointer',
                        padding: '0',
                        fontSize: '14px',
                        textAlign: 'left',
                        width: '100%',
                        borderRadius: '4px',
                        transition: 'background-color 0.2s'
                      }}
                    >
                      {email.subject}
                    </button>
                  </td>
                  <td style={{ padding: '12px' }}>{email.recipient_count} subscribers</td>
                  <td style={{ padding: '12px' }}>{formatDateTime(email.sent_at)}</td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: email.status === 'sent' ? '#d4edda' : '#f8d7da',
                      color: email.status === 'sent' ? '#155724' : '#721c24'
                    }}>
                      {email.status.charAt(0).toUpperCase() + email.status.slice(1)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div className="bulk-email-section">
      <div className="section-header" style={{ marginBottom: '30px' }}>
        <h1 style={{ marginBottom: '20px' }}>Bulk Email</h1>
        
        <div className="tabs" style={{ display: 'flex', borderBottom: '2px solid #e0e0e0' }}>
          <button
            onClick={() => setActiveTab('compose')}
            style={{
              padding: '12px 24px',
              border: 'none',
              backgroundColor: activeTab === 'compose' ? '#007bff' : 'transparent',
              color: activeTab === 'compose' ? 'white' : '#666',
              cursor: 'pointer',
              fontWeight: activeTab === 'compose' ? '600' : '400',
              borderRadius: '6px 6px 0 0',
              marginRight: '5px'
            }}
          >
            Compose Email
          </button>
          <button
            onClick={() => setActiveTab('sent')}
            style={{
              padding: '12px 24px',
              border: 'none',
              backgroundColor: activeTab === 'sent' ? '#007bff' : 'transparent',
              color: activeTab === 'sent' ? 'white' : '#666',
              cursor: 'pointer',
              fontWeight: activeTab === 'sent' ? '600' : '400',
              borderRadius: '6px 6px 0 0'
            }}
          >
            Sent Emails
          </button>
        </div>
      </div>

      <div className="tab-content" style={{ marginTop: '20px' }}>
        {activeTab === 'compose' ? renderComposeTab() : renderSentEmailsTab()}
      </div>

      {showEmailDetails && selectedEmail && (
        <div className="email-details-modal" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="email-details-content" style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '90%',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
            position: 'relative'
          }}>
            <button
              onClick={closeEmailDetails}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'none',
                border: 'none',
                color: '#007bff',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              &times;
            </button>

            <h3 style={{ marginTop: 0, marginBottom: '15px' }}>{selectedEmail.subject}</h3>

            <div style={{ marginBottom: '15px', color: '#666' }}>
              <strong>Recipients:</strong> {selectedEmail.recipient_count} subscribers
            </div>

            <div style={{ marginBottom: '15px', color: '#666' }}>
              <strong>Sent Date:</strong> {formatDateTime(selectedEmail.sent_at)}
            </div>

            <div style={{ marginBottom: '15px', color: '#666' }}>
              <strong>Status:</strong> 
              <span style={{
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '500',
                backgroundColor: selectedEmail.status === 'sent' ? '#d4edda' : '#f8d7da',
                color: selectedEmail.status === 'sent' ? '#155724' : '#721c24'
              }}>
                {selectedEmail.status.charAt(0).toUpperCase() + selectedEmail.status.slice(1)}
              </span>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong>Message Body:</strong>
              <div style={{ 
                padding: '10px',
                borderRadius: '4px',
                backgroundColor: '#f8f9fa',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {selectedEmail.body}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkEmailSection;
